import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import { SubscriptionFormProps } from '.';
import { useUserCreate } from '../../hooks';
import { RECAPTCHA_SITE_KEY } from '../../src/env';
import { LeadType } from '../../utils/backend-api/users';
import { splitInitialDob } from '../../utils/dates';
import { extractQueryParams } from '../../utils/queryStringHelper';
import { YOUNG_PROFESSIONALS_PLAN } from '../../utils/subscriptionCardOptions';
import { Button, CustomSpinner } from '../BaseUI';
import { TextField } from '../FormFields';
import DateOfBirthField from './DateOfBirthField';
import styles from './styles.module.scss';
import validationSchema from './validationSchema';

interface FormValues {
  day: { label: number; value: number };
  dob: string;
  email: string;
  month: { label: string; value: number };
  name: string;
  userEmail?: string;
  year: { label: number; value: number };
}

interface SignupFormProps extends SubscriptionFormProps {
  initialDob?: string;
  setStripeCustomerId: (stripeCustomerId: string) => void;
  setDob?: (dob: string) => void;
}

const SignupForm = ({
  dob,
  initialDob,
  referringSubscriberGuid,
  selectedOffer: { code },
  setStripeCustomerId,
  setDob,
  userFullName,
  userEmail,
}: SignupFormProps) => {
  const [disableNameAndEmailInputs, setDisableNameAndEmailInputs] = useState(
    !!userFullName && !!userEmail
  );
  const [disableDobInput, setDisableDobInput] = useState(true);
  const { mutateAsync: createUser, isLoading } = useUserCreate();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const formRef = useRef<FormikProps<FormValues>>();
  const { article_id: referringArticleId } = extractQueryParams(['article_id']);
  const { initialDay, initialMonth, initialYear } = splitInitialDob(initialDob);
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
  const leadType = referringSubscriberGuid ? LeadType.REFERRAL_PAGE : LeadType.SUBSCRIBE_PAGE;

  useEffect(() => {
    if (isYPPlan && !dob) {
      setDisableDobInput(false);
    } else {
      setDisableDobInput(true);
    }
  }, [isYPPlan, dob]);

  // Passed userEmail to the form for email validation initial state
  const initialState = {
    day: initialDay,
    dob: '',
    email: userEmail || '',
    month: initialMonth,
    name: userFullName || '',
    userEmail,
    year: initialYear,
  };

  const handleCreateUser = async ({ day, email, month, name, year }: FormValues) => {
    try {
      const refToken = await reCaptchaRef.current.executeAsync();
      const dobString = isYPPlan ? `${year.value}-${month.value}-${day.value}` : null;
      const createUserParams = {
        dob: dobString,
        email,
        lead_type: leadType,
        name,
        code,
        recaptcha_token: refToken,
        referring_article_id: referringArticleId,
      };
      const { stripeCustomerId } = await createUser(createUserParams);
      setDisableNameAndEmailInputs(true);
      setDisableDobInput(true);
      setStripeCustomerId(stripeCustomerId);
      setDob(dobString);
      reCaptchaRef.current.reset();
    } catch {
      setDisableNameAndEmailInputs(false);
      setDisableDobInput(false);
      reCaptchaRef.current.reset();
    }
  };

  return (
    <div className={styles.subscriptionForm}>
      <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
        <Formik
          enableReinitialize
          initialValues={initialState}
          onSubmit={handleCreateUser}
          validationSchema={validationSchema(isYPPlan)}
          innerRef={formRef}
        >
          {({ handleSubmit, isValid, errors, touched }) => (
            <Form className="account-info" noValidate onSubmit={handleSubmit}>
              <div className="tw-mt-24 tw-mb-20 tw-flex tw-items-center">
                <div className="tw-text-primary-red tw-text-body-sm tw-uppercase tw-font-bold tw-font-sans">
                  Account Information
                </div>
                <div className="tw-ml-12 tw-flex-1 tw-bg-primary-red tw-h-2"></div>
              </div>
              <TextField
                name="name"
                placeholder="Full Name"
                disabled={userFullName || disableNameAndEmailInputs}
              />
              <TextField
                name="email"
                placeholder="Email"
                disabled={userEmail || disableNameAndEmailInputs}
              />
              {isYPPlan && (
                <DateOfBirthField
                  disableDobInput={disableDobInput}
                  error={
                    touched.month && touched.day && touched.year && errors?.dob ? errors.dob : null
                  }
                />
              )}
              <Button
                id="signupBtn"
                disabled={isLoading || !isValid || (disableNameAndEmailInputs && disableDobInput)}
                type="submit"
                className={clsx(styles.submitBtn, 'tw-mt-16')}
              >
                {isLoading ? (
                  <CustomSpinner
                    color="#fff"
                    size={10}
                    style={{ display: 'flex', justifyContent: 'center' }}
                  />
                ) : (
                  <>Continue</>
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </ReCAPTCHA>
    </div>
  );
};

export default SignupForm;
